import { SlideshowSelectEvent } from '@theme/events';

/**
 * Tabbed featured collection: switches product carousels and wires toolbar arrows.
 */
class FeaturedCollectionTabs extends HTMLElement {
  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    this.tabs = this.querySelectorAll('[data-collection-filter]');
    this.panels = this.querySelectorAll('[data-collection-panel]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.dotsContainer = this.querySelector('[data-carousel-dots]');
    this.carouselControls = this.querySelector('[data-carousel-controls]');
    this.shopAllLink = this.querySelector('[data-shop-all-link]');

    if (!this.panels.length) return;

    if (this.tabs.length) {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.#setTab(tab.dataset.collectionFilter);
        });
      });

      const initialTab = this.querySelector('[data-collection-filter][aria-selected="true"]') ?? this.tabs[0];
      this.#setTab(initialTab?.dataset.collectionFilter);
    } else {
      this.#updateSlideshowViewport();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#syncControls());
      });
    }

    this.prevButton?.addEventListener('click', this.#handlePrev);
    this.nextButton?.addEventListener('click', this.#handleNext);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect);
    this.#mobileMediaQuery.addEventListener('change', this.#syncControls);
    window.addEventListener('resize', this.#syncControls, { passive: true });
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.#handlePrev);
    this.nextButton?.removeEventListener('click', this.#handleNext);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect);
    this.#mobileMediaQuery.removeEventListener('change', this.#syncControls);
    window.removeEventListener('resize', this.#syncControls);
  }

  /** @param {MouseEvent} event */
  #handlePrev = (event) => {
    event.preventDefault();
    const slideshow = this.#activeSlideshow();
    if (!slideshow || this.prevButton?.disabled) return;

    this.#prepareSlideshow(slideshow);
    slideshow.previous(event);
    requestAnimationFrame(() => this.#syncControls());
  };

  /** @param {MouseEvent} event */
  #handleNext = (event) => {
    event.preventDefault();
    const slideshow = this.#activeSlideshow();
    if (!slideshow || this.nextButton?.disabled) return;

    this.#prepareSlideshow(slideshow);
    slideshow.next(event);
    requestAnimationFrame(() => this.#syncControls());
  };

  /** @param {CustomEvent} event */
  #onSlideshowSelect = (event) => {
    const slideshow = this.#activeSlideshow();
    if (!slideshow || event.target !== slideshow) return;

    if (this.#usesMobileDots()) {
      this.#syncDotsState();
      return;
    }

    this.#syncArrowState();
  };

  /**
   * @param {string} filterId
   */
  #setTab(filterId) {
    if (!filterId) return;

    const matchingPanel = this.querySelector(`[data-collection-panel="${filterId}"]`);
    if (!matchingPanel) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.collectionFilter === filterId;
      tab.classList.toggle('featured-collection-tabs__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.collectionPanel === filterId;
      panel.hidden = !isActive;
      panel.toggleAttribute('inert', !isActive);
    });

    const activePanel = this.querySelector(`[data-collection-panel="${filterId}"]:not([hidden])`);
    const collectionUrl = activePanel?.dataset.collectionUrl;

    if (this.shopAllLink && collectionUrl) {
      this.shopAllLink.href = collectionUrl;
    }

    this.#toggleCarouselControls(activePanel?.hasAttribute('data-collection-empty') ?? false);

    this.#updateSlideshowViewport();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#syncControls());
    });
  }

  #updateSlideshowViewport() {
    this.querySelectorAll('[data-collection-panel] slideshow-component').forEach((slideshow) => {
      const panel = slideshow.closest('[data-collection-panel]');
      const isActive = panel && !panel.hidden;

      if (isActive) {
        this.#prepareSlideshow(slideshow);
      } else {
        slideshow.removeAttribute('in-viewport');
      }
    });
  }

  /**
   * @param {HTMLElement} slideshow
   */
  #prepareSlideshow(slideshow) {
    slideshow.setAttribute('in-viewport', '');
  }

  #activeSlideshow() {
    const panel = this.querySelector('[data-collection-panel]:not([hidden])');
    const slideshow = panel?.querySelector('slideshow-component');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  /**
   * @param {HTMLElement} slideshow
   */
  #canScrollSlideshow(slideshow) {
    const scroller = slideshow.refs?.scroller ?? slideshow.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return false;

    return scroller.scrollWidth > scroller.clientWidth + 1;
  }

  #usesMobileDots() {
    return this.#mobileMediaQuery.matches;
  }

  /**
   * @param {boolean} isEmpty
   */
  #toggleCarouselControls(isEmpty) {
    if (this.carouselControls) {
      this.carouselControls.hidden = isEmpty;
    }

    if (this.dotsContainer) {
      this.dotsContainer.hidden = isEmpty;
    }
  }

  #syncControls = () => {
    const activePanel = this.querySelector('[data-collection-panel]:not([hidden])');
    if (activePanel?.hasAttribute('data-collection-empty')) {
      this.#toggleCarouselControls(true);
      return;
    }

    this.#toggleCarouselControls(false);

    if (this.#usesMobileDots()) {
      this.#buildDots();
      this.#syncDotsState();
      return;
    }

    if (this.dotsContainer) {
      this.dotsContainer.hidden = true;
      this.dotsContainer.innerHTML = '';
    }

    this.#syncArrowState();
  };

  #buildDots() {
    const slideshow = this.#activeSlideshow();
    if (!this.dotsContainer || !slideshow) return;

    const slideCount = slideshow.slides?.length ?? 0;
    this.dotsContainer.innerHTML = '';

    if (slideCount <= 1) {
      this.dotsContainer.hidden = true;
      return;
    }

    const list = document.createElement('div');
    list.className = 'featured-collection-tabs__dots-list';
    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-label', 'Product carousel pagination');

    for (let index = 0; index < slideCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'featured-collection-tabs__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.setAttribute('aria-selected', index === slideshow.current ? 'true' : 'false');
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.#prepareSlideshow(slideshow);
        slideshow.select(index, event);
      });
      list.appendChild(dot);
    }

    this.dotsContainer.appendChild(list);
    this.dotsContainer.hidden = false;
  }

  #syncDotsState() {
    if (!this.dotsContainer || this.dotsContainer.hidden) return;

    const slideshow = this.#activeSlideshow();
    if (!slideshow) return;

    this.dotsContainer.querySelectorAll('.featured-collection-tabs__dot').forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === slideshow.current ? 'true' : 'false');
    });
  }

  #syncArrowState = () => {
    const slideshow = this.#activeSlideshow();
    if (!slideshow || !this.prevButton || !this.nextButton) return;

    const slides = slideshow.slides;
    const slideCount = slides?.length ?? 0;
    const canScroll = this.#canScrollSlideshow(slideshow);

    if (!canScroll || slideCount <= 1) {
      this.#setArrowDisabled(this.prevButton, true);
      this.#setArrowDisabled(this.nextButton, true);
      return;
    }

    const atStart = slideshow.current <= 0;
    const atEnd = slideshow.nextIndex >= slideCount;

    this.#setArrowDisabled(this.prevButton, atStart);
    this.#setArrowDisabled(this.nextButton, atEnd);
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} disabled
   */
  #setArrowDisabled(button, disabled) {
    button.disabled = disabled;
    button.classList.toggle('featured-collection-tabs__arrow--disabled', disabled);
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }
}

if (!customElements.get('featured-collection-tabs')) {
  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
}
